import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalHeading,
  ModalBody,
  ModalFooter,
  ModalFooterActions,
  Button,
  Label,
  Input,
  Select,
  Option,
  Box,
  HelpText,
  TextArea,
  Checkbox,
  CheckboxGroup,
} from "@twilio-paste/core";
import {
  TemplateDto,
  TemplateVariableDto,
  TemplateVariableType,
} from "../../dto/template";
import { TeamDto } from "../../dto/team";
import { TemplateCategoryDto } from "../../dto/templateCategory";
import {
  CreateTemplateRequest,
  UpdateTemplateRequest,
  createTemplate,
  updateTemplate,
} from "../../api/adminApi";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSaved: (t: TemplateDto) => void;
  template?: TemplateDto | null;
  teams: TeamDto[];
  categories: TemplateCategoryDto[];
};

const VARIABLE_TYPES = ["string", "datetime", "date", "time", "number"];

const TemplateFormModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onSaved,
  template,
  teams,
  categories,
}) => {
  const isEdit = !!template;

  const [name, setName] = useState("");
  const [content, setContent] = useState("");
  const [templateSid, setTemplateSid] = useState("");
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);
  const [categoryId, setCategoryId] = useState<string>("");
  const [isActive, setIsActive] = useState(true);
  const [variables, setVariables] = useState<TemplateVariableDto[]>([]);
  const [newVarKey, setNewVarKey] = useState("");
  const [newVarType, setNewVarType] = useState<TemplateVariableType>("string");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (template) {
      setName(template.name);
      setContent(template.content);
      setTemplateSid(template.templateSid);
      setSelectedTeamIds(template.teams.map((t) => t.id));
      setCategoryId(template.categoryId ?? "");
      setIsActive(template.isActive);
      setVariables([...template.variables]);
    } else {
      setName("");
      setContent("");
      setTemplateSid("");
      setSelectedTeamIds([]);
      setCategoryId("");
      setIsActive(true);
      setVariables([]);
    }
    setNewVarKey("");
    setNewVarType("string");
    setError(null);
  }, [template, isOpen]);

  const toggleTeam = (id: string) => {
    setSelectedTeamIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const addVariable = () => {
    if (!newVarKey.trim()) return;
    setVariables((prev) => [
      ...prev,
      { key: newVarKey.trim(), type: newVarType },
    ]);
    setNewVarKey("");
    setNewVarType("string");
  };

  const removeVariable = (index: number) => {
    setVariables((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!name.trim() || !content.trim() || !templateSid.trim()) {
      setError("Nome, contenuto e Template SID sono obbligatori.");
      return;
    }

    setSaving(true);
    setError(null);
    try {
      let saved: TemplateDto;
      const base = {
        name,
        content,
        templateSid,
        teamIds: selectedTeamIds,
        variables,
        categoryId: categoryId || null,
      };
      if (isEdit && template) {
        const req: UpdateTemplateRequest = { ...base, isActive };
        saved = await updateTemplate(template.id, req);
      } else {
        const req: CreateTemplateRequest = base;
        saved = await createTemplate(req);
      }
      onSaved(saved);
      onClose();
    } catch (e: any) {
      setError(
        e?.response?.data?.detail ??
          e?.message ??
          "Errore durante il salvataggio."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      ariaLabelledby="template-form-modal"
      isOpen={isOpen}
      onDismiss={onClose}
      size="wide"
    >
      <ModalHeader>
        <ModalHeading as="h3">
          {isEdit ? "Modifica template" : "Nuovo template"}
        </ModalHeading>
      </ModalHeader>
      <ModalBody>
        <Box display="flex" flexDirection="column" rowGap="space50">
          <Box>
            <Label htmlFor="tmpl-name" required>
              Nome
            </Label>
            <Input
              id="tmpl-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Box>
          <Box>
            <Label htmlFor="tmpl-sid" required>
              Template SID (Twilio)
            </Label>
            <Input
              id="tmpl-sid"
              type="text"
              value={templateSid}
              onChange={(e) => setTemplateSid(e.target.value)}
            />
          </Box>
          <Box>
            <Label htmlFor="tmpl-content" required>
              Contenuto
            </Label>
            <TextArea
              id="tmpl-content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={4}
            />
          </Box>
          <Box>
            <Label htmlFor="tmpl-category">Categoria</Label>
            <Select
              id="tmpl-category"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
            >
              <Option value="">— Nessuna —</Option>
              {categories.map((c) => (
                <Option key={c.id} value={c.id}>
                  {c.name}
                </Option>
              ))}
            </Select>
          </Box>
          <Box>
            <CheckboxGroup
              name="tmpl-teams"
              legend="Team abilitati"
              helpText="Seleziona i team che possono usare questo template"
            >
              {teams.map((t) => (
                <Checkbox
                  key={t.id}
                  id={`tmpl-team-${t.id}`}
                  value={t.id}
                  checked={selectedTeamIds.includes(t.id)}
                  onChange={() => toggleTeam(t.id)}
                >
                  {t.name}
                </Checkbox>
              ))}
            </CheckboxGroup>
          </Box>
          <Box>
            <Label as="legend">Variabili</Label>
            {variables.length > 0 && (
              <Box
                as="ul"
                paddingLeft="space0"
                marginBottom="space30"
                style={{ listStyle: "none" }}
              >
                {variables.map((v, i) => (
                  <Box
                    key={i}
                    as="li"
                    display="flex"
                    alignItems="center"
                    columnGap="space30"
                    marginBottom="space20"
                  >
                    <Box
                      backgroundColor="colorBackgroundBody"
                      borderRadius="borderRadius20"
                      padding="space20"
                      fontSize="fontSize20"
                      fontFamily="fontFamilyCode"
                      flexGrow={1}
                    >
                      {`{{${v.key}}}`} — {v.type}
                    </Box>
                    <Button
                      variant="destructive_secondary"
                      size="small"
                      onClick={() => removeVariable(i)}
                    >
                      Rimuovi
                    </Button>
                  </Box>
                ))}
              </Box>
            )}
            <Box display="flex" columnGap="space30" alignItems="flex-end">
              <Box flexGrow={1}>
                <Label htmlFor="var-key">Chiave variabile</Label>
                <Input
                  id="var-key"
                  type="text"
                  placeholder="es. nome"
                  value={newVarKey}
                  onChange={(e) => setNewVarKey(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addVariable()}
                />
              </Box>
              <Box>
                <Label htmlFor="var-type">Tipo</Label>
                <Select
                  id="var-type"
                  value={newVarType}
                  onChange={(e) =>
                    setNewVarType(e.target.value as TemplateVariableType)
                  }
                >
                  {VARIABLE_TYPES.map((t) => (
                    <Option key={t} value={t}>
                      {t}
                    </Option>
                  ))}
                </Select>
              </Box>
              <Button variant="secondary" onClick={addVariable}>
                Aggiungi
              </Button>
            </Box>
          </Box>
          {isEdit && (
            <Box>
              <Label htmlFor="tmpl-active">Stato</Label>
              <Select
                id="tmpl-active"
                value={isActive ? "true" : "false"}
                onChange={(e) => setIsActive(e.target.value === "true")}
              >
                <Option value="true">Attivo</Option>
                <Option value="false">Disabilitato</Option>
              </Select>
            </Box>
          )}
          {error && <HelpText variant="error">{error}</HelpText>}
        </Box>
      </ModalBody>
      <ModalFooter>
        <ModalFooterActions>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Annulla
          </Button>
          <Button variant="primary" onClick={handleSave} loading={saving}>
            {isEdit ? "Salva" : "Crea"}
          </Button>
        </ModalFooterActions>
      </ModalFooter>
    </Modal>
  );
};

export default TemplateFormModal;
